import { AccordionStep, ExtraAssetOption } from '@hb/domain';
import { ExtraAssetBuilder } from '@hb/builder';
import { AbstractStoreStepInject } from '@hb/services/store/abstract-store-step-inject.service';
import { FieldSubject } from '@hb/domain/field-subject';
import { StoreService } from '@hb/services/store/store.service';
import { AccordionStateService } from '@hb/services/accordion-state/accordion-state.service';

import { BehaviorSubject, Observable, skip } from 'rxjs';
import { inject, Injectable, OnDestroy } from '@angular/core';

import { ExtraAsset } from '@ap/ap-interface-apix-hypotheken-berekening';
import { Money } from '@ap/interface-bspc-core';

@Injectable({
    providedIn: 'root'
})
export class ExtraAssetStoreService extends AbstractStoreStepInject implements OnDestroy {
    protected readonly storeService = inject(StoreService);
    protected readonly accordionStateService = inject(AccordionStateService);

    private readonly extraAssetSubject: BehaviorSubject<ExtraAsset>;

    private readonly ownPropertySubject: FieldSubject<boolean>;
    private readonly sellingPriceSubject: FieldSubject<Money>;
    private readonly mortgageAmountSubject: FieldSubject<Money>;
    private readonly ownMoneySubject: FieldSubject<boolean>;
    private readonly ownMoneyAmountSubject: FieldSubject<Money>;

    constructor() {
        super();
        this.summary = ExtraAssetOption.SUMMARY;
        this.activeOptionSubject = new BehaviorSubject(
            this.accordionStateService.getStepIsFinished(AccordionStep.EXTRA_ASSET)
                ? ExtraAssetOption.SUMMARY
                : ExtraAssetOption.OWN_PROPERTY
        );

        const extraAssetState = this.getExtraAssetState();

        this.extraAssetSubject = new BehaviorSubject<ExtraAsset>(extraAssetState);
        this.ownPropertySubject = new FieldSubject<boolean>(extraAssetState?.own_property);
        this.sellingPriceSubject = new FieldSubject<Money>(extraAssetState?.selling_price);
        this.mortgageAmountSubject = new FieldSubject<Money>(extraAssetState?.mortgage_amount);
        this.ownMoneySubject = new FieldSubject<boolean>(extraAssetState?.own_money);
        this.ownMoneyAmountSubject = new FieldSubject<Money>(extraAssetState?.own_money_amount);

        this.setUp();
    }

    setUp(): void {
        this.getExtraAsset()
            .pipe(skip(1))
            .subscribe((extraAsset: ExtraAsset) => this.storeService.setExtraAsset(extraAsset));
        this.getOwnProperty().subscribe(() => this.update());
        this.getSellingPrice().subscribe(() => this.update());
        this.getMortgageAmount().subscribe(() => this.update());
        this.getOwnMoney().subscribe(() => this.update());
        this.getOwnMoneyAmount().subscribe(() => this.update());
    }

    getExtraAssetState(): ExtraAsset {
        return this.storeService.getState()?.extraAsset;
    }

    getExtraAsset(): Observable<ExtraAsset> {
        return this.extraAssetSubject.asObservable();
    }

    getOwnProperty(): Observable<boolean> {
        return this.ownPropertySubject.asObservable();
    }

    getSellingPrice(): Observable<Money> {
        return this.sellingPriceSubject.asObservable();
    }

    getMortgageAmount(): Observable<Money> {
        return this.mortgageAmountSubject.asObservable();
    }

    getOwnMoney(): Observable<boolean> {
        return this.ownMoneySubject.asObservable();
    }

    getOwnMoneyAmount(): Observable<Money> {
        return this.ownMoneyAmountSubject.asObservable();
    }

    setOwnProperty(ownProperty: boolean): void {
        if (!ownProperty) {
            this.sellingPriceSubject.next(null);
            this.mortgageAmountSubject.next(null);
        }
        this.ownPropertySubject.next(ownProperty);
    }

    setSellingPrice(sellingPrice: Money): void {
        this.sellingPriceSubject.next(sellingPrice);
    }

    setMortgageAmount(mortgageAmount: Money): void {
        this.mortgageAmountSubject.next(mortgageAmount);
    }

    setOwnMoney(ownMoney: boolean): void {
        if (!ownMoney) {
            this.ownMoneyAmountSubject.next(null);
        }
        this.ownMoneySubject.next(ownMoney);
    }

    setOwnMoneyAmount(ownMoneyAmount: Money): void {
        this.ownMoneyAmountSubject.next(ownMoneyAmount);
    }

    ngOnDestroy(): void {
        this.activeOptionSubject.unsubscribe();
        this.extraAssetSubject.unsubscribe();
        this.ownPropertySubject.unsubscribe();
        this.sellingPriceSubject.unsubscribe();
        this.mortgageAmountSubject.unsubscribe();
        this.ownMoneySubject.unsubscribe();
        this.ownMoneyAmountSubject.unsubscribe();
    }

    isActiveOption(question: ExtraAssetOption): boolean {
        switch (question) {
            case ExtraAssetOption.OWN_PROPERTY:
            case ExtraAssetOption.OWN_MONEY:
                return true;
            case ExtraAssetOption.SUMMARY:
            default:
                return false;
        }
    }

    private update(): void {
        this.extraAssetSubject.next(
            new ExtraAssetBuilder()
                .withOwnProperty(this.ownPropertySubject.value)
                .withSellingPrice(this.sellingPriceSubject.value)
                .withMortgageAmount(this.mortgageAmountSubject.value)
                .withOwnMoney(this.ownMoneySubject.value)
                .withOwnMoneyAmount(this.ownMoneyAmountSubject.value)
                .build()
        );
    }
}
